import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  LogOut, Users, Inbox, PhoneCall, CheckCheck, Loader2, Mail, Phone, Trash2,
  Search, Star, Plus, ClipboardCheck,
} from "lucide-react";

const statusStyle = {
  new: "bg-[#0055FF]/15 text-[#3377FF] border-[#0055FF]/40",
  reviewed: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  contacted: "bg-green-500/15 text-green-400 border-green-500/40",
};

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, reviewed: 0, contacted: 0 });
  const [checkins, setCheckins] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, s, c, t] = await Promise.all([
        api.get("/applications"),
        api.get("/applications/stats"),
        api.get("/checkins"),
        api.get("/testimonials/all"),
      ]);
      setApps(a.data); setStats(s.data); setCheckins(c.data); setTestimonials(t.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ht_token");
        navigate("/admin/login");
      } else toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("ht_token")) { navigate("/admin/login"); return; }
    load();
  }, [load, navigate]);

  const logout = () => { localStorage.removeItem("ht_token"); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      setApps((p) => p.map((a) => (a.id === id ? data : a)));
      if (selected?.id === id) setSelected(data);
      const s = await api.get("/applications/stats"); setStats(s.data);
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/${deleteTarget.type}/${deleteTarget.id}`);
      if (deleteTarget.type === "applications") {
        setApps((p) => p.filter((a) => a.id !== deleteTarget.id));
        if (selected?.id === deleteTarget.id) setSelected(null);
        const s = await api.get("/applications/stats"); setStats(s.data);
      } else if (deleteTarget.type === "checkins") {
        setCheckins((p) => p.filter((a) => a.id !== deleteTarget.id));
        if (selectedCheckin?.id === deleteTarget.id) setSelectedCheckin(null);
      } else {
        setTestimonials((p) => p.filter((a) => a.id !== deleteTarget.id));
      }
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleting(false); setDeleteTarget(null); }
  };

  const filtered = apps
    .filter((a) => filter === "all" || a.status === filter)
    .filter((a) => {
      const q = search.toLowerCase().trim();
      return !q || a.full_name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q) || (a.goal || "").toLowerCase().includes(q);
    });

  const cards = [
    { label: "Total", value: stats.total, icon: Users },
    { label: "New", value: stats.new, icon: Inbox },
    { label: "Reviewed", value: stats.reviewed, icon: CheckCheck },
    { label: "Contacted", value: stats.contacted, icon: PhoneCall },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="border-b border-[#262626] bg-[#0A0A0A]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg tracking-tight text-white">HOWELL</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0055FF] font-bold">Coach Dashboard</div>
          </div>
          <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white border border-[#262626] hover:border-[#0055FF] px-4 py-2 transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-[#0055FF]" size={32} /></div>
        ) : (
          <Tabs defaultValue="applications">
            <TabsList className="bg-[#141414] border border-[#262626] rounded-none h-auto p-1 flex-wrap">
              <TabsTrigger data-testid="tab-applications" value="applications" className="rounded-none data-[state=active]:bg-[#0055FF] data-[state=active]:text-white uppercase text-xs tracking-widest font-bold px-4 py-2">Applications</TabsTrigger>
              <TabsTrigger data-testid="tab-checkins" value="checkins" className="rounded-none data-[state=active]:bg-[#0055FF] data-[state=active]:text-white uppercase text-xs tracking-widest font-bold px-4 py-2">Check-Ins</TabsTrigger>
              <TabsTrigger data-testid="tab-testimonials" value="testimonials" className="rounded-none data-[state=active]:bg-[#0055FF] data-[state=active]:text-white uppercase text-xs tracking-widest font-bold px-4 py-2">Testimonials</TabsTrigger>
            </TabsList>

            {/* APPLICATIONS */}
            <TabsContent value="applications" className="mt-8">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map((c) => (
                  <div key={c.label} data-testid={`stat-${c.label.toLowerCase()}`} className="bg-[#141414] border border-[#262626] p-6">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{c.label}</span>
                      <c.icon size={18} className="text-[#0055FF]" />
                    </div>
                    <div className="font-display font-bold text-3xl text-white mt-3">{c.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                  <Input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, or goal" className="bg-[#141414] border-[#262626] text-white rounded-none h-10 pl-9 focus-visible:ring-0 focus-visible:border-[#0055FF]" />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger data-testid="filter-select" className="w-full sm:w-44 bg-[#141414] border-[#262626] text-white rounded-none h-10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626] text-white">
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filtered.length === 0 ? (
                <div data-testid="no-applications" className="mt-16 text-center text-[#A3A3A3]">No applications found.</div>
              ) : (
                <div className="mt-6 space-y-3">
                  {filtered.map((a) => (
                    <button key={a.id} data-testid={`application-row-${a.id}`} onClick={() => setSelected(a)} className="w-full text-left bg-[#141414] border border-[#262626] hover:border-[#0055FF] transition-colors p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="font-display font-semibold text-white">{a.full_name}</div>
                        <div className="text-sm text-[#A3A3A3] mt-1">{a.goal}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-[#A3A3A3] hidden sm:block">{new Date(a.created_at).toLocaleDateString()}</span>
                        <Badge className={`rounded-none border uppercase text-[10px] tracking-widest ${statusStyle[a.status]}`}>{a.status}</Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* CHECK-INS */}
            <TabsContent value="checkins" className="mt-8">
              <div className="flex items-center gap-2 mb-6">
                <ClipboardCheck size={18} className="text-[#0055FF]" />
                <h2 className="font-display font-semibold text-xl text-white uppercase tracking-tight">Weekly Check-Ins ({checkins.length})</h2>
              </div>
              {checkins.length === 0 ? (
                <div data-testid="no-checkins" className="mt-10 text-center text-[#A3A3A3]">No check-ins yet.</div>
              ) : (
                <div className="space-y-3">
                  {checkins.map((c) => (
                    <button key={c.id} data-testid={`checkin-row-${c.id}`} onClick={() => setSelectedCheckin(c)} className="w-full text-left bg-[#141414] border border-[#262626] hover:border-[#0055FF] transition-colors p-5 flex items-center justify-between gap-3">
                      <div>
                        <div className="font-display font-semibold text-white">{c.client_name}</div>
                        <div className="text-sm text-[#A3A3A3] mt-1">Weight: {c.current_weight} · {c.photos?.length || 0} photo(s)</div>
                      </div>
                      <span className="text-xs text-[#A3A3A3]">{new Date(c.created_at).toLocaleDateString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* TESTIMONIALS */}
            <TabsContent value="testimonials" className="mt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-semibold text-xl text-white uppercase tracking-tight">Testimonials ({testimonials.length})</h2>
                <button data-testid="add-testimonial-button" onClick={() => setShowTestimonialForm(true)} className="inline-flex items-center gap-2 bg-[#0055FF] text-white hover:bg-[#3377FF] font-bold px-4 py-2 uppercase tracking-widest text-xs transition-colors">
                  <Plus size={14} /> Add
                </button>
              </div>
              {testimonials.length === 0 ? (
                <div data-testid="no-testimonials" className="mt-10 text-center text-[#A3A3A3]">No testimonials yet. Add your first client success story.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {testimonials.map((t) => (
                    <div key={t.id} data-testid={`testimonial-row-${t.id}`} className="bg-[#141414] border border-[#262626] p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-display font-semibold text-white">{t.name}</div>
                          <div className="flex gap-0.5 mt-1">{[...Array(5)].map((_, s) => <Star key={s} size={13} className={s < t.rating ? "fill-[#0055FF] text-[#0055FF]" : "text-[#262626]"} />)}</div>
                        </div>
                        <button data-testid={`delete-testimonial-${t.id}`} onClick={() => setDeleteTarget({ type: "testimonials", id: t.id, label: t.name })} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                      </div>
                      <p className="text-sm text-[#A3A3A3] mt-3 leading-relaxed">{t.review}</p>
                      {t.result && <div className="text-xs uppercase tracking-widest text-[#0055FF] font-bold mt-2">{t.result}</div>}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Application detail */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="application-detail-dialog" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[85vh] overflow-y-auto rounded-none">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase tracking-tight">{selected.full_name}</DialogTitle>
                <DialogDescription className="text-[#A3A3A3]">Submitted {new Date(selected.created_at).toLocaleString()}</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-4 py-2">
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 text-sm text-[#3377FF] hover:underline"><Mail size={15} /> {selected.email}</a>
                <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 text-sm text-[#3377FF] hover:underline"><Phone size={15} /> {selected.phone}</a>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 mt-2">
                {[["Age", selected.age], ["Height", selected.height], ["Current Weight", selected.current_weight], ["Gym or Home", selected.training_location], ["Experience", selected.experience_level], ["Ready To Start", selected.ready_to_start], ["Program Interest", selected.program_interest || "—"], ["Injuries", selected.injuries || "None"], ["Agreed to Policies", selected.legal_agreed ? "Yes" : "No"]].map(([k, v]) => (
                  <div key={k}><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">{k}</div><div className="text-white mt-1">{v}</div></div>
                ))}
              </div>
              <div className="mt-4"><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">Goal</div><div className="text-white mt-1">{selected.goal}</div></div>
              <div className="mt-4"><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">Biggest Struggle</div><p className="text-white mt-1 leading-relaxed">{selected.biggest_struggle}</p></div>
              {selected.about_you && <div className="mt-4"><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">About</div><p className="text-white mt-1 leading-relaxed">{selected.about_you}</p></div>}
              <div className="mt-6 pt-5 border-t border-[#262626]">
                <div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-3">Update Status</div>
                <div className="flex flex-wrap gap-3">
                  {["new", "reviewed", "contacted"].map((s) => (
                    <button key={s} data-testid={`set-status-${s}`} onClick={() => updateStatus(selected.id, s)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${selected.status === s ? "bg-[#0055FF] border-[#0055FF] text-white" : "border-[#262626] text-[#A3A3A3] hover:border-[#0055FF]"}`}>{s}</button>
                  ))}
                </div>
              </div>
              <div className="mt-4 pt-5 border-t border-[#262626]">
                <button data-testid="delete-application-button" onClick={() => setDeleteTarget({ type: "applications", id: selected.id, label: selected.full_name })} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-colors">
                  <Trash2 size={14} /> Delete Application
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-in detail */}
      <Dialog open={!!selectedCheckin} onOpenChange={(o) => !o && setSelectedCheckin(null)}>
        <DialogContent data-testid="checkin-detail-dialog" className="bg-[#141414] border-[#262626] text-white max-w-2xl max-h-[85vh] overflow-y-auto rounded-none">
          {selectedCheckin && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase tracking-tight">{selectedCheckin.client_name}</DialogTitle>
                <DialogDescription className="text-[#A3A3A3]">Check-in {new Date(selectedCheckin.created_at).toLocaleString()}</DialogDescription>
              </DialogHeader>
              <a href={`mailto:${selectedCheckin.client_email}`} className="inline-flex items-center gap-2 text-sm text-[#3377FF] hover:underline"><Mail size={15} /> {selectedCheckin.client_email}</a>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 mt-4">
                {[["Weight", selectedCheckin.current_weight], ["Sleep", selectedCheckin.sleep || "—"], ["Energy", selectedCheckin.energy || "—"], ["Hunger", selectedCheckin.hunger || "—"], ["Workouts", selectedCheckin.workout_completion || "—"], ["Cardio", selectedCheckin.cardio_completion || "—"]].map(([k, v]) => (
                  <div key={k}><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">{k}</div><div className="text-white mt-1">{v}</div></div>
                ))}
              </div>
              {selectedCheckin.notes && <div className="mt-4"><div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold">Questions / Concerns</div><p className="text-white mt-1 leading-relaxed">{selectedCheckin.notes}</p></div>}
              {selectedCheckin.photos?.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2">Progress Photos</div>
                  <div className="flex flex-wrap gap-3">
                    {selectedCheckin.photos.map((p, i) => <img key={i} src={p} alt={`Progress ${i + 1}`} className="w-28 h-36 object-cover border border-[#262626]" />)}
                  </div>
                </div>
              )}
              <div className="mt-6 pt-5 border-t border-[#262626]">
                <button data-testid="delete-checkin-button" onClick={() => setDeleteTarget({ type: "checkins", id: selectedCheckin.id, label: selectedCheckin.client_name })} className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:border-red-500 transition-colors">
                  <Trash2 size={14} /> Delete Check-In
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add testimonial */}
      <AddTestimonialDialog open={showTestimonialForm} onClose={() => setShowTestimonialForm(false)} onAdded={(t) => setTestimonials((p) => [t, ...p])} />

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog" className="bg-[#141414] border-[#262626] text-white rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Delete this item?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A3A3]">This permanently removes {deleteTarget?.label}. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" className="rounded-none bg-transparent border-[#262626] text-white hover:bg-[#0A0A0A] hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" onClick={doDelete} disabled={deleting} className="rounded-none bg-red-600 text-white hover:bg-red-700">{deleting ? "Deleting..." : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddTestimonialDialog({ open, onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", rating: 5, review: "", result: "", before_image: "", after_image: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const fieldCls = "bg-[#0A0A0A] border-[#262626] focus-visible:border-[#0055FF] focus-visible:ring-0 text-white rounded-none";

  const onImg = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, [k]: r.result }));
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name.trim() || !form.review.trim()) { toast.error("Name and review are required."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/testimonials", { ...form, rating: Number(form.rating) });
      onAdded(data);
      toast.success("Testimonial added");
      setForm({ name: "", rating: 5, review: "", result: "", before_image: "", after_image: "" });
      onClose();
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-testimonial-dialog" className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[85vh] overflow-y-auto rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Testimonial</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">Add a client success story with optional before/after photos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Client Name *</Label><Input data-testid="testimonial-name" className={`${fieldCls} h-11`} value={form.name} onChange={set("name")} /></div>
          <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Rating</Label>
            <Select value={String(form.rating)} onValueChange={(v) => setForm((f) => ({ ...f, rating: v }))}>
              <SelectTrigger data-testid="testimonial-rating" className={`${fieldCls} h-11`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626] text-white">{[5,4,3,2,1].map((n) => <SelectItem key={n} value={String(n)}>{n} Stars</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Result (e.g. Lost 35 lbs)</Label><Input data-testid="testimonial-result" className={`${fieldCls} h-11`} value={form.result} onChange={set("result")} /></div>
          <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Review *</Label><Textarea data-testid="testimonial-review" className={`${fieldCls} h-24 py-2`} value={form.review} onChange={set("review")} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Before Photo</Label><input data-testid="testimonial-before" type="file" accept="image/*" onChange={onImg("before_image")} className="text-xs text-[#A3A3A3]" />{form.before_image && <img src={form.before_image} alt="before" className="w-16 h-20 object-cover border border-[#262626]" />}</div>
            <div className="space-y-2"><Label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">After Photo</Label><input data-testid="testimonial-after" type="file" accept="image/*" onChange={onImg("after_image")} className="text-xs text-[#A3A3A3]" />{form.after_image && <img src={form.after_image} alt="after" className="w-16 h-20 object-cover border border-[#262626]" />}</div>
          </div>
          <button data-testid="save-testimonial-button" onClick={save} disabled={saving} className="w-full bg-[#0055FF] text-white hover:bg-[#3377FF] font-bold px-6 py-3 uppercase tracking-wide text-sm transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save Testimonial"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
